/**
 * briefingSchemas.js — Mini-schémas SVG contextuels pour la vue Briefing.
 *
 * Ces SVGs sont :
 *  - statiques (pas de framer-motion) → rendu instantané
 *  - en couleur d'accent (la catégorie REMC) → cohérence visuelle
 *  - design "fil au trait" + remplissage doux → cahier pédagogique
 *  - mobile-first (viewBox 200x120, lisibles en 5 secondes)
 *
 * Utilisation :
 *   import { getBriefingSchema } from '@/data/teacher/briefingSchemas';
 *   const Schema = getBriefingSchema(code); // ou null
 *   <Schema accent={accent} />
 */
import React from 'react';

/* ─── Schémas réutilisables ─────────────────────────────────────── */

export function SchemaRegardProjection({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma regard / projection">
      {/* sol */}
      <line x1="10" y1="100" x2="190" y2="100" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1.5" />
      {/* moto stylisée (rond + barre) */}
      <circle cx="40" cy="92" r="6" fill={accent} />
      <rect x="32" y="78" width="16" height="14" rx="2" fill={accent} fillOpacity="0.85" />
      {/* tête pilote */}
      <circle cx="40" cy="70" r="5" fill="none" stroke={accent} strokeWidth="1.8" />
      {/* faisceau regard */}
      <path d="M 45 67 Q 110 35 175 28" fill="none" stroke={accent} strokeWidth="2" strokeDasharray="4 3" />
      {/* point de visée */}
      <circle cx="175" cy="28" r="5" fill="none" stroke={accent} strokeWidth="2.5" />
      <circle cx="175" cy="28" r="2" fill={accent} />
      <text x="175" y="48" fontSize="9" fontWeight="700" fill={accent} textAnchor="middle">VISÉE</text>
    </svg>
  );
}

export function SchemaTrajectoireVirage({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma trajectoire de virage">
      {/* route extérieure */}
      <path d="M 10 100 Q 100 100 130 60 L 145 25" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="22" />
      {/* lignes blanches */}
      <path d="M 10 100 Q 100 100 130 60 L 145 25" fill="none" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="4 4" />
      {/* trajectoire idéale extérieur-intérieur-extérieur */}
      <path d="M 20 105 Q 110 95 122 55 L 138 20" fill="none" stroke={accent} strokeWidth="2.5" />
      {/* moto */}
      <circle cx="20" cy="105" r="4" fill={accent} />
      {/* point de corde */}
      <circle cx="122" cy="55" r="3.5" fill={accent} />
      <text x="155" y="65" fontSize="9" fontWeight="700" fill={accent}>CORDE</text>
    </svg>
  );
}

export function SchemaAllureCompteur({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma adaptation de l'allure">
      {/* cadran */}
      <circle cx="100" cy="80" r="42" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="3" />
      {/* arc rouge (excès) */}
      <path d="M 142 80 A 42 42 0 0 0 130 50" fill="none" stroke="#dc2626" strokeWidth="6" />
      {/* arc vert (zone bonne) */}
      <path d="M 58 80 A 42 42 0 0 1 125 47" fill="none" stroke="#16a34a" strokeWidth="6" />
      {/* aiguille */}
      <line x1="100" y1="80" x2="80" y2="50" stroke={accent} strokeWidth="3" strokeLinecap="round" />
      <circle cx="100" cy="80" r="4" fill={accent} />
      {/* étiquettes */}
      <text x="56" y="100" fontSize="8" fontWeight="700" fill="#16a34a">ADAPTÉ</text>
      <text x="142" y="100" fontSize="8" fontWeight="700" fill="#dc2626" textAnchor="end">EXCÈS</text>
    </svg>
  );
}

export function SchemaFreinage7030({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma freinage 70/30">
      <text x="10" y="22" fontSize="10" fontWeight="900" fill="#16a34a">AVANT 70%</text>
      <rect x="10" y="28" width="180" height="14" rx="3" fill="currentColor" fillOpacity="0.08" />
      <rect x="10" y="28" width="126" height="14" rx="3" fill="#16a34a" />
      <text x="10" y="68" fontSize="10" fontWeight="900" fill="#dc2626">ARRIÈRE 30%</text>
      <rect x="10" y="74" width="180" height="14" rx="3" fill="currentColor" fillOpacity="0.08" />
      <rect x="10" y="74" width="54" height="14" rx="3" fill="#dc2626" />
      <text x="10" y="108" fontSize="9" fontWeight="700" fill={accent}>RÉPARTITION CIBLE</text>
    </svg>
  );
}

export function SchemaEquilibreSlalom({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma équilibre slalom">
      <line x1="10" y1="100" x2="190" y2="100" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1.5" />
      {/* cônes */}
      {[40, 80, 120, 160].map((x, i) => (
        <g key={i}>
          <path d={`M ${x} 100 L ${x - 5} 80 L ${x + 5} 80 Z`} fill={accent} fillOpacity="0.5" />
          <circle cx={x} cy="78" r="2" fill={accent} />
        </g>
      ))}
      {/* trajectoire serpentine */}
      <path d="M 15 88 Q 40 64 60 86 Q 80 108 100 86 Q 120 64 140 86 Q 160 108 180 88" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" />
      {/* moto position */}
      <circle cx="15" cy="88" r="4" fill={accent} />
    </svg>
  );
}

export function SchemaDistanceSecurite({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma distance de sécurité">
      <line x1="10" y1="80" x2="190" y2="80" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1.5" strokeDasharray="3 3" />
      {/* moto devant */}
      <g transform="translate(150,55)">
        <circle cx="0" cy="20" r="6" fill="currentColor" fillOpacity="0.5" />
        <rect x="-8" y="6" width="16" height="14" rx="2" fill="currentColor" fillOpacity="0.5" />
        <circle cx="0" cy="-2" r="4" fill="none" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.6" />
      </g>
      {/* moto derrière */}
      <g transform="translate(40,55)">
        <circle cx="0" cy="20" r="6" fill={accent} />
        <rect x="-8" y="6" width="16" height="14" rx="2" fill={accent} />
        <circle cx="0" cy="-2" r="4" fill="none" stroke={accent} strokeWidth="2" />
      </g>
      {/* mesure distance */}
      <line x1="50" y1="100" x2="140" y2="100" stroke={accent} strokeWidth="2" markerStart="url(#arrL)" markerEnd="url(#arrR)" />
      <defs>
        <marker id="arrL" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 8 1 L 2 5 L 8 9 Z" fill={accent} />
        </marker>
        <marker id="arrR" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 2 1 L 8 5 L 2 9 Z" fill={accent} />
        </marker>
      </defs>
      <text x="95" y="115" fontSize="9" fontWeight="800" fill={accent} textAnchor="middle">2 SECONDES MIN.</text>
    </svg>
  );
}

export function SchemaIntersection({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma intersection">
      {/* route verticale */}
      <rect x="85" y="10" width="30" height="100" fill="currentColor" fillOpacity="0.1" />
      {/* route horizontale */}
      <rect x="10" y="50" width="180" height="30" fill="currentColor" fillOpacity="0.1" />
      {/* lignes pointillées centrales */}
      <line x1="100" y1="10" x2="100" y2="50" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 3" />
      <line x1="100" y1="80" x2="100" y2="110" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 3" />
      <line x1="10" y1="65" x2="85" y2="65" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 3" />
      <line x1="115" y1="65" x2="190" y2="65" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="4 3" />
      {/* trajectoire moto avec contrôle */}
      <path d="M 100 110 Q 100 80 100 65" fill="none" stroke={accent} strokeWidth="2.5" />
      {/* moto */}
      <circle cx="100" cy="110" r="4" fill={accent} />
      {/* arc de contrôle visuel droite */}
      <path d="M 105 70 Q 130 70 145 70" fill="none" stroke={accent} strokeWidth="1.5" strokeDasharray="3 2" />
      <circle cx="148" cy="70" r="3" fill="none" stroke={accent} strokeWidth="1.8" />
      {/* arc gauche */}
      <path d="M 95 70 Q 70 70 55 70" fill="none" stroke={accent} strokeWidth="1.5" strokeDasharray="3 2" />
      <circle cx="52" cy="70" r="3" fill="none" stroke={accent} strokeWidth="1.8" />
      <text x="100" y="30" fontSize="9" fontWeight="800" fill={accent} textAnchor="middle">CONTRÔLE 360°</text>
    </svg>
  );
}

export function SchemaPositionnementVoie({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma positionnement sur la voie">
      {/* route */}
      <rect x="20" y="20" width="160" height="80" fill="currentColor" fillOpacity="0.08" rx="2" />
      {/* ligne médiane intouchable */}
      <line x1="100" y1="20" x2="100" y2="100" stroke="#dc2626" strokeWidth="2" strokeDasharray="6 4" />
      {/* zones 1/3 — droite recommandée hors agglo */}
      <rect x="20" y="20" width="26" height="80" fill={accent} fillOpacity="0.2" />
      <rect x="50" y="20" width="48" height="80" fill={accent} fillOpacity="0.06" />
      {/* moto bonne position 1/3 droite (mais dans la voie droite!) */}
      <g transform="translate(155,60)">
        <circle cx="0" cy="0" r="6" fill={accent} />
        <rect x="-7" y="-14" width="14" height="14" rx="2" fill={accent} />
      </g>
      <text x="155" y="92" fontSize="8" fontWeight="800" fill={accent} textAnchor="middle">1/3 DROIT</text>
      <text x="35" y="115" fontSize="7" fontWeight="700" fill="#dc2626">interdit</text>
      <text x="103" y="15" fontSize="7" fontWeight="800" fill="#dc2626">ligne médiane</text>
    </svg>
  );
}

export function SchemaMeteoPluie({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma météo pluie">
      {/* nuage */}
      <ellipse cx="100" cy="30" rx="42" ry="14" fill="currentColor" fillOpacity="0.2" />
      <ellipse cx="80" cy="26" rx="22" ry="12" fill="currentColor" fillOpacity="0.2" />
      <ellipse cx="120" cy="26" rx="22" ry="12" fill="currentColor" fillOpacity="0.2" />
      {/* gouttes */}
      {[
        [70, 50], [85, 60], [100, 50], [115, 60], [130, 50],
        [78, 75], [108, 80], [122, 75],
      ].map(([x, y], i) => (
        <path key={i} d={`M ${x} ${y} Q ${x - 2} ${y + 6} ${x} ${y + 9} Q ${x + 2} ${y + 6} ${x} ${y}`} fill={accent} />
      ))}
      {/* sol mouillé */}
      <line x1="10" y1="105" x2="190" y2="105" stroke={accent} strokeWidth="2.5" strokeOpacity="0.5" />
      <text x="100" y="118" fontSize="8" fontWeight="800" fill={accent} textAnchor="middle">ADHÉRENCE RÉDUITE</text>
    </svg>
  );
}

export function SchemaNuitPhares({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma conduite de nuit">
      {/* ciel sombre */}
      <rect x="0" y="0" width="200" height="100" fill="currentColor" fillOpacity="0.15" />
      <line x1="0" y1="100" x2="200" y2="100" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" />
      {/* cône lumineux */}
      <path d="M 40 90 L 180 50 L 180 100 L 40 100 Z" fill={accent} fillOpacity="0.3" />
      <path d="M 40 90 L 180 50 L 180 100 L 40 100 Z" fill="url(#beam)" opacity="0.4" />
      <defs>
        <linearGradient id="beam" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#fde047" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#fde047" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* moto */}
      <g transform="translate(30,85)">
        <circle cx="0" cy="0" r="5" fill={accent} />
        <rect x="-6" y="-12" width="12" height="12" rx="2" fill={accent} />
      </g>
      {/* étoile */}
      <text x="150" y="30" fontSize="16" fill="currentColor" fillOpacity="0.4">★</text>
      <text x="100" y="116" fontSize="8" fontWeight="800" fill={accent} textAnchor="middle">VISIBILITÉ RÉDUITE</text>
    </svg>
  );
}

export function SchemaManoeuvreArret({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma manœuvre démarrage / arrêt">
      <line x1="10" y1="90" x2="190" y2="90" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1.5" />
      {/* repère arrêt */}
      <line x1="150" y1="70" x2="150" y2="105" stroke="#dc2626" strokeWidth="2.5" />
      <text x="150" y="118" fontSize="8" fontWeight="800" fill="#dc2626" textAnchor="middle">REPÈRE</text>
      {/* flèche progression */}
      <path d="M 30 80 L 142 80" fill="none" stroke={accent} strokeWidth="2.5" markerEnd="url(#fwd)" />
      <defs>
        <marker id="fwd" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 Z" fill={accent} />
        </marker>
      </defs>
      {/* moto */}
      <g transform="translate(30,80)">
        <circle cx="0" cy="0" r="5" fill={accent} />
        <rect x="-6" y="-12" width="12" height="12" rx="2" fill={accent} />
      </g>
      <text x="80" y="65" fontSize="9" fontWeight="800" fill={accent}>≤ 1 m du repère</text>
    </svg>
  );
}

export function SchemaControleRetro({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma contrôle rétro / angle mort">
      {/* moto vue de dessus */}
      <g transform="translate(100,70)">
        <rect x="-12" y="-26" width="24" height="46" rx="6" fill={accent} fillOpacity="0.85" />
        <rect x="-7" y="-30" width="14" height="6" rx="2" fill={accent} />
        {/* rétros */}
        <circle cx="-15" cy="-18" r="3" fill={accent} stroke="white" strokeWidth="1.2" />
        <circle cx="15" cy="-18" r="3" fill={accent} stroke="white" strokeWidth="1.2" />
      </g>
      {/* faisceaux rétros */}
      <path d="M 85 52 L 30 30" fill="none" stroke={accent} strokeWidth="1.5" strokeDasharray="3 2" />
      <path d="M 85 52 L 30 75" fill="none" stroke={accent} strokeWidth="1.5" strokeDasharray="3 2" />
      <path d="M 115 52 L 170 30" fill="none" stroke={accent} strokeWidth="1.5" strokeDasharray="3 2" />
      <path d="M 115 52 L 170 75" fill="none" stroke={accent} strokeWidth="1.5" strokeDasharray="3 2" />
      {/* angles morts */}
      <path d="M 85 52 L 30 75 L 70 105 L 90 95 Z" fill="#dc2626" fillOpacity="0.18" />
      <path d="M 115 52 L 170 75 L 130 105 L 110 95 Z" fill="#dc2626" fillOpacity="0.18" />
      <text x="100" y="118" fontSize="8" fontWeight="800" fill={accent} textAnchor="middle">CONTRÔLE RÉTRO + ANGLE MORT</text>
    </svg>
  );
}

/* ─── C3-e — Cohabiter avec les usagers vulnérables ───────────── */
export function SchemaUsagersVulnerables({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma cohabitation usagers vulnérables">
      {/* route */}
      <rect x="10" y="55" width="180" height="50" fill="currentColor" fillOpacity="0.08" />
      <line x1="10" y1="80" x2="190" y2="80" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="6 4" />
      {/* trottoir bas (piéton) */}
      <rect x="10" y="100" width="180" height="10" fill="currentColor" fillOpacity="0.18" />
      {/* piéton */}
      <g transform="translate(35,95)">
        <circle cx="0" cy="-8" r="3.5" fill="#dc2626" />
        <line x1="0" y1="-5" x2="0" y2="5" stroke="#dc2626" strokeWidth="2.2" strokeLinecap="round" />
        <line x1="-3" y1="-1" x2="3" y2="-1" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
        <line x1="0" y1="5" x2="-2" y2="11" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
        <line x1="0" y1="5" x2="2" y2="11" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
      </g>
      {/* cycliste */}
      <g transform="translate(95,75)">
        <circle cx="-6" cy="0" r="4.5" fill="none" stroke="#16a34a" strokeWidth="1.6" />
        <circle cx="6" cy="0" r="4.5" fill="none" stroke="#16a34a" strokeWidth="1.6" />
        <line x1="-6" y1="0" x2="6" y2="0" stroke="#16a34a" strokeWidth="1.5" />
        <line x1="0" y1="-4" x2="3" y2="-10" stroke="#16a34a" strokeWidth="1.5" />
        <circle cx="3" cy="-13" r="3" fill="#16a34a" />
      </g>
      {/* moto (à 1 m mini) */}
      <g transform="translate(160,72)">
        <circle cx="0" cy="6" r="5" fill={accent} />
        <rect x="-7" y="-8" width="14" height="14" rx="2" fill={accent} />
        <circle cx="0" cy="-12" r="4" fill="none" stroke={accent} strokeWidth="1.8" />
      </g>
      {/* distance latérale */}
      <path d="M 110 70 L 150 70" stroke={accent} strokeWidth="1.8" markerEnd="url(#arrUv)" />
      <defs>
        <marker id="arrUv" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 Z" fill={accent} />
        </marker>
      </defs>
      <text x="130" y="64" fontSize="8" fontWeight="800" fill={accent} textAnchor="middle">≥ 1 m</text>
      <text x="100" y="22" fontSize="9" fontWeight="800" fill={accent} textAnchor="middle">DISTANCE LATÉRALE</text>
      {/* indices visuels */}
      <circle cx="160" cy="44" r="6" fill="none" stroke={accent} strokeWidth="1.5" />
      <text x="160" y="48" fontSize="8" fontWeight="900" fill={accent} textAnchor="middle">!</text>
    </svg>
  );
}

/* ─── C3-g — S'insérer et sortir d'une voie rapide ───────────── */
export function SchemaInsertionVoieRapide({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma insertion voie rapide">
      {/* autoroute (3 voies horizontales) */}
      <rect x="10" y="35" width="180" height="50" fill="currentColor" fillOpacity="0.08" />
      <line x1="10" y1="52" x2="190" y2="52" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="6 4" />
      <line x1="10" y1="68" x2="190" y2="68" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="6 4" />
      {/* bordures autoroute */}
      <line x1="10" y1="35" x2="190" y2="35" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" />
      <line x1="10" y1="85" x2="190" y2="85" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" />
      {/* bretelle d'insertion */}
      <path d="M 10 110 Q 60 110 100 85" stroke="currentColor" strokeOpacity="0.18" strokeWidth="14" fill="none" />
      <path d="M 10 110 Q 60 110 100 85" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="4 4" fill="none" />
      {/* voiture déjà sur autoroute */}
      <g transform="translate(155,77)">
        <rect x="-10" y="-3" width="20" height="6" rx="1.5" fill="currentColor" fillOpacity="0.55" />
        <rect x="-7" y="-6" width="14" height="3" fill="currentColor" fillOpacity="0.55" />
      </g>
      {/* trajectoire moto accélération */}
      <path d="M 18 108 Q 60 108 95 82 L 130 75" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" markerEnd="url(#arrIns)" />
      <defs>
        <marker id="arrIns" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 Z" fill={accent} />
        </marker>
      </defs>
      {/* moto au début */}
      <g transform="translate(20,108)">
        <circle cx="0" cy="0" r="4" fill={accent} />
        <rect x="-5" y="-9" width="10" height="9" rx="1.5" fill={accent} />
      </g>
      {/* étapes */}
      <text x="40" y="20" fontSize="8" fontWeight="800" fill={accent}>1. CONTRÔLE</text>
      <text x="100" y="20" fontSize="8" fontWeight="800" fill={accent}>2. CLIGNOTANT</text>
      <text x="158" y="20" fontSize="8" fontWeight="800" fill={accent}>3. ACCÉLÈRE</text>
      <line x1="40" y1="23" x2="195" y2="23" stroke={accent} strokeOpacity="0.3" strokeWidth="0.8" />
    </svg>
  );
}

/* ─── C4-b — Gérer un trajet longue distance ──────────────────── */
export function SchemaTrajetLongueDistance({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma trajet longue distance">
      {/* tracé itinéraire (sinueux) */}
      <path
        d="M 18 95 Q 50 65 75 75 Q 100 85 120 55 Q 145 25 178 30"
        fill="none"
        stroke={accent}
        strokeWidth="2.5"
        strokeDasharray="5 3"
        strokeLinecap="round"
      />
      {/* point départ */}
      <g transform="translate(18,95)">
        <circle cx="0" cy="0" r="5" fill={accent} />
        <circle cx="0" cy="0" r="2" fill="white" />
        <text x="0" y="20" fontSize="7" fontWeight="800" fill={accent} textAnchor="middle">DÉPART</text>
      </g>
      {/* pauses (étapes café) */}
      <g transform="translate(75,75)">
        <circle cx="0" cy="0" r="6" fill="white" stroke="#f59e0b" strokeWidth="2" />
        <text x="0" y="3" fontSize="9" fontWeight="900" fill="#f59e0b" textAnchor="middle">☕</text>
        <text x="0" y="20" fontSize="7" fontWeight="800" fill="#f59e0b" textAnchor="middle">2 h</text>
      </g>
      <g transform="translate(120,55)">
        <circle cx="0" cy="0" r="6" fill="white" stroke="#f59e0b" strokeWidth="2" />
        <text x="0" y="3" fontSize="9" fontWeight="900" fill="#f59e0b" textAnchor="middle">☕</text>
        <text x="0" y="20" fontSize="7" fontWeight="800" fill="#f59e0b" textAnchor="middle">4 h</text>
      </g>
      {/* arrivée */}
      <g transform="translate(178,30)">
        <path d="M -5 5 L 0 -7 L 5 5 Z" fill="#16a34a" />
        <circle cx="0" cy="6" r="3" fill="#16a34a" />
        <text x="0" y="-12" fontSize="7" fontWeight="800" fill="#16a34a" textAnchor="middle">ARRIVÉE</text>
      </g>
      {/* total */}
      <text x="100" y="115" fontSize="8" fontWeight="900" fill={accent} textAnchor="middle">PAUSE 15 MIN / 2 H DE ROUTE</text>
    </svg>
  );
}

/* ─── C4-c — Facteurs de risque humains ──────────────────────── */
export function SchemaRisquesHumains({ accent = '#0891b2' }) {
  return (
    <svg viewBox="0 0 200 120" className="w-full h-full" aria-label="Schéma facteurs de risque humains">
      {/* triangle warning */}
      <path d="M 100 18 L 138 92 L 62 92 Z" fill={accent} fillOpacity="0.1" stroke={accent} strokeWidth="2" strokeLinejoin="round" />
      <text x="100" y="78" fontSize="40" fontWeight="900" fill={accent} textAnchor="middle">!</text>
      {/* 4 facteurs autour */}
      {/* fatigue */}
      <g transform="translate(28,28)">
        <circle cx="0" cy="0" r="11" fill="white" stroke="#dc2626" strokeWidth="1.8" />
        <text x="0" y="3.5" fontSize="11" textAnchor="middle">💤</text>
      </g>
      <text x="28" y="50" fontSize="7" fontWeight="800" fill="#dc2626" textAnchor="middle">FATIGUE</text>
      {/* alcool */}
      <g transform="translate(172,28)">
        <circle cx="0" cy="0" r="11" fill="white" stroke="#dc2626" strokeWidth="1.8" />
        <text x="0" y="3.5" fontSize="11" textAnchor="middle">🍺</text>
      </g>
      <text x="172" y="50" fontSize="7" fontWeight="800" fill="#dc2626" textAnchor="middle">ALCOOL</text>
      {/* téléphone */}
      <g transform="translate(28,95)">
        <circle cx="0" cy="0" r="11" fill="white" stroke="#dc2626" strokeWidth="1.8" />
        <text x="0" y="3.5" fontSize="11" textAnchor="middle">📱</text>
      </g>
      <text x="28" y="117" fontSize="7" fontWeight="800" fill="#dc2626" textAnchor="middle">DISTRACTION</text>
      {/* drogues / médicaments */}
      <g transform="translate(172,95)">
        <circle cx="0" cy="0" r="11" fill="white" stroke="#dc2626" strokeWidth="1.8" />
        <text x="0" y="3.5" fontSize="11" textAnchor="middle">💊</text>
      </g>
      <text x="172" y="117" fontSize="7" fontWeight="800" fill="#dc2626" textAnchor="middle">SUBSTANCES</text>
    </svg>
  );
}

/* ─── Mapping code REMC → composant schéma ───────────────────────── */

const SCHEMA_BY_CODE = {
  // C1 — Maîtrise mécanique
  'C1-a': SchemaManoeuvreArret,
  'C1-b': SchemaManoeuvreArret,
  'C1-c': SchemaEquilibreSlalom,
  'C1-d': SchemaEquilibreSlalom,
  'C1-e': SchemaManoeuvreArret,
  'C1-f': SchemaManoeuvreArret,
  'C1-g': SchemaFreinage7030,
  'C1-h': SchemaFreinage7030,

  // C2 — Appréhender la route
  'C2-a': SchemaRegardProjection,
  'C2-b': SchemaRegardProjection,
  'C2-c': SchemaTrajectoireVirage,
  'C2-d': SchemaAllureCompteur,
  'C2-e': SchemaPositionnementVoie,
  'C2-f': SchemaPositionnementVoie,
  'C2-g': SchemaDistanceSecurite,
  'C2-h': SchemaControleRetro,

  // C3 — Circuler dans des conditions difficiles
  'C3-a': SchemaIntersection,
  'C3-b': SchemaIntersection,
  'C3-c': SchemaTrajectoireVirage,
  'C3-d': SchemaTrajectoireVirage,
  'C3-e': SchemaUsagersVulnerables,
  'C3-f': SchemaControleRetro,
  'C3-g': SchemaInsertionVoieRapide,
  'C3-h': SchemaIntersection,

  // C4 — Pratiquer une conduite autonome, sûre et économique
  'C4-a': SchemaMeteoPluie,
  'C4-b': SchemaTrajetLongueDistance,
  'C4-c': SchemaRisquesHumains,
  'C4-d': SchemaNuitPhares,
  'C4-e': SchemaNuitPhares,
  'C4-f': SchemaControleRetro,
  'C4-g': SchemaAllureCompteur,
};

/**
 * Retourne le composant schéma associé à un code REMC, ou null si aucun.
 * Usage : const Schema = getBriefingSchema(code); Schema && <Schema accent={...} />
 */
export function getBriefingSchema(code) {
  if (!code) return null;
  return SCHEMA_BY_CODE[code] || null;
}

export default getBriefingSchema;
